import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import WorkCard from "@/components/feature/WorkCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getPopularWorks, searchWorks } from "@/utils/videoUtils";

const PAGE_SIZE = 8;

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [page, setPage] = useState(1);

  usePageMeta(
    query ? `“${query}” 搜索结果 - 日本伦理剧` : "搜索影视作品 - 日本伦理剧",
    query
      ? `日本伦理剧中与“${query}”相关的伦理剧情作品搜索结果，涵盖电视剧与电影。`
      : "在日本伦理剧中搜索你感兴趣的伦理剧情电视剧与电影作品。",
    "日本伦理剧搜索,伦理剧情,日本影视,作品检索",
  );

  useEffect(() => {
    setInput(query);
    setPage(1);
  }, [query]);

  const results = useMemo(() => searchWorks(query), [query]);
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hotWorks = getPopularWorks(4);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
  };

  return (
    <div className="w-full px-4 md:px-6 py-8">
      <h1 className="font-heading text-2xl font-bold text-foreground-950">搜索作品</h1>
      <p className="mt-1 text-sm text-foreground-600">支持剧名、导演、演员、题材等关键词的模糊匹配</p>

      <form onSubmit={handleSubmit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-lg"></i>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入关键词，如：治愈、家庭、山田和也"
            aria-label="搜索影视内容"
            className="w-full h-11 pl-10 pr-3 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <button
          type="submit"
          className="h-11 px-7 rounded-md bg-primary-700 text-background-50 font-label text-sm hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          搜索
        </button>
      </form>

      {query && (
        <p className="mt-6 text-sm font-label text-foreground-600">
          关键词“<span className="text-primary-700 font-semibold">{query}</span>”
          {results.length > 0 ? (
            <>
              ，共找到 <span className="text-primary-700 font-semibold">{results.length}</span> 部相关作品
            </>
          ) : (
            " 没有找到匹配的作品"
          )}
        </p>
      )}

      {!query ? (
        <div className="mt-10">
          <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4">大家都在看</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {hotWorks.map((work) => (
              <WorkCard key={work.id} work={work} showViews />
            ))}
          </div>
        </div>
      ) : results.length === 0 ? (
        <div className="mt-10 rounded-lg border border-background-200/70 bg-background-50 py-20 text-center">
          <i className="ri-search-eye-line text-5xl text-foreground-400"></i>
          <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">未找到匹配的作品</h2>
          <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或浏览下面的热门作品</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-700 text-background-50 font-label text-sm hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
            <Link
              to="/category/drama"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-primary-300 bg-background-50 text-primary-700 font-label text-sm hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览全部作品
            </Link>
          </div>
          <div className="mt-10 max-w-4xl mx-auto text-left">
            <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4 px-4">热门作品推荐</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 px-4">
              {hotWorks.map((work) => (
                <WorkCard key={work.id} work={work} showViews />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {pageItems.map((work) => (
              <WorkCard key={work.id} work={work} showViews />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`w-10 h-10 rounded-md text-sm font-label border transition-colors cursor-pointer ${
                    p === page
                      ? "bg-primary-700 text-background-50 border-primary-700"
                      : "bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}